import { DisclosureFilter } from "../disclosureFilter/DisclosureFilter.tsx";
import { useAppSelector } from "../../../../../../stores/hooks/hooks.ts";
import { faMagnifyingGlass } from "@fortawesome/pro-light-svg-icons";
import { ChangeEvent, useEffect, useState } from "react";
import {
  RevButton,
  RevCard,
  RevCheckbox,
  RevChip,
  RevEmptyState,
  RevInput,
  RevSelect,
  RevSpinner,
} from "@revmatics/catalyst";
import { FilterParamsType } from "../../../../discoverCommunitiesTypes.ts";
import { useDebounce } from "../../../../../../helpers/hooks/useDebounce.tsx";
import { getFitName } from "../../../../../../helpers/functions/getFitName.ts";
import { CommunityCheckboxCard } from "./components/CommunityCheckboxCard.tsx";

const SIZE_FILTER_OPTIONS = [
  { value: "size", label: "Size- smallest to largest" },
  { value: "-size", label: "Size- largest to smallest" },
];
const AFFILIATION_FILTER_CHIP = [
  { value: "high", title: "High" },
  { value: "medium", title: "Medium" },
  { value: "low", title: "Low" },
];
type Props = {
  selectedCommunity: Set<number>;
  setSelectedCommunity: (value: Set<number>) => void;
  selectedInterests: Set<string>;
  setSelectedInterests: (value: Set<string>) => void;
  filter: FilterParamsType;
  handleChangeFilter: (key: string, value: string) => void;
  selectedAffiliation: Set<string>;
  setSelectedAffiliation: (value: Set<string>) => void;
  selectedInterestsCommunity: Set<string>;
  setSelectedInterestsCommunity: (value: Set<string>) => void;
};
export const CommunityDisclosureFilter = ({
  selectedCommunity,
  setSelectedCommunity,
  selectedInterests,
  selectedInterestsCommunity,
  setSelectedInterestsCommunity,
  filter,
  handleChangeFilter,
  selectedAffiliation,
  setSelectedAffiliation,
}: Props) => {
  const [search, setSearch] = useState("");
  const [minSize, setMinSize] = useState(filter.min_size || "");
  const [maxSize, setMaxSize] = useState(filter.max_size || "");
  const debouncedSearch = useDebounce(search, 500);
  const debouncedMinSize = useDebounce(minSize, 500);
  const debouncedMaxSize = useDebounce(maxSize, 500);
  const communities = useAppSelector(
    (state) => state.discoverCommunities.communities,
  );
  const isLoadingCommunity = useAppSelector(
    (state) => state.discoverCommunities.isLoadingCommunities,
  );

  useEffect(() => {
    handleChangeFilter("name", debouncedSearch);
  }, [debouncedSearch, handleChangeFilter]);

  useEffect(() => {
    handleChangeFilter("min_size", debouncedMinSize);
  }, [debouncedMinSize, handleChangeFilter]);

  useEffect(() => {
    handleChangeFilter("max_size", debouncedMaxSize);
  }, [debouncedMaxSize, handleChangeFilter]);

  useEffect(() => {
    if (!filter.min_size) {
      setMinSize("");
    }
    if (!filter.max_size) {
      setMaxSize("");
    }
    if (!filter.name) {
      setSearch("");
    }
  }, [filter]);

  return (
    <DisclosureFilter
      title="Community Associations"
      tooltip="Select one or more communities"
      selectedList={
        communities && communities.length > 0
          ? [...selectedCommunity].map(
              (item) =>
                communities?.find((elem) => elem.community.id === item)
                  ?.community.name || "",
            )
          : []
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(250px,_3fr)_7fr] gap-x-6 gap-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-4">
          <RevInput
            name="name"
            type="text"
            label=""
            size="md"
            placeholder="Search community"
            iconLeft={faMagnifyingGlass}
            enableClearButton
            value={search}
            onClear={() => setSearch("")}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />
          <RevSelect
            name="sort"
            label=""
            sorting
            value={filter.sort}
            onChange={(value) =>
              handleChangeFilter("sort", value?.toString() || "size")
            }
            options={SIZE_FILTER_OPTIONS}
          />
          <RevCard padding="p-3">
            <div className="flex items-center justify-between mb-3 h-6">
              <p className="typography-body-bold-sm text-monochrome-900">
                Size
              </p>
              {(filter.min_size || filter.max_size) && (
                <RevButton
                  displayAs="link"
                  text="Clear Filters"
                  className="w-fit"
                  linkFontSize="small"
                  underline={false}
                  handleClick={() => {
                    setMaxSize("");
                    setMinSize("");
                  }}
                />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <RevInput
                name="min_size"
                type="text"
                label="Min"
                size="md"
                value={minSize}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMinSize(e.target.value.toString() || "")
                }
                mask="currency"
              />
              <RevInput
                name="max_size"
                type="text"
                label="Max"
                size="md"
                value={maxSize}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setMaxSize(e.target.value.toString() || "")
                }
                mask="currency"
              />
            </div>
          </RevCard>
          <RevCard padding="p-3 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3 h-6">
              <p className="typography-body-bold-sm text-monochrome-900">Fit</p>
              {selectedAffiliation.size > 0 && (
                <RevButton
                  displayAs="link"
                  text="Clear Filters"
                  className="w-fit"
                  linkFontSize="small"
                  underline={false}
                  handleClick={() => setSelectedAffiliation(new Set())}
                />
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {AFFILIATION_FILTER_CHIP.map((item) => (
                <RevChip
                  key={`community-affiliation-chip-${item.value}`}
                  text={getFitName(item.title.toLowerCase())}
                  selected={selectedAffiliation.has(item.value)}
                  handleClick={() => {
                    const updatedSet = new Set(selectedAffiliation);
                    if (updatedSet.has(item.value)) {
                      updatedSet.delete(item.value);
                    } else {
                      updatedSet.add(item.value);
                    }
                    setSelectedAffiliation(updatedSet);
                  }}
                />
              ))}
            </div>
          </RevCard>
        </div>

        <div className="py-4 rounded-lg bg-monochrome-910">
          <div className="flex items-center gap-4 mb-4 px-3">
            <RevCheckbox
              label="All"
              full
              checked={
                !!communities &&
                communities.length > 0 &&
                selectedCommunity.size > 0 &&
                selectedCommunity.size < communities.length
              }
              checkedFull={
                !!communities &&
                communities.length > 0 &&
                selectedCommunity.size === communities.length
              }
              handleChecked={() => {
                if (communities && communities.length > 0) {
                  if (selectedCommunity.size === communities.length) {
                    setSelectedCommunity(new Set());
                  } else {
                    setSelectedCommunity(
                      new Set(communities.map((item) => item.community.id)),
                    );
                  }
                  if (selectedInterestsCommunity.size > 0) {
                    setSelectedInterestsCommunity(new Set());
                  }
                }
              }}
            />
            {isLoadingCommunity && (
              <RevSpinner
                size="medium"
                color="primary"
                data-testid="spinner-communities"
              />
            )}
          </div>
          {communities && communities.length > 0 ? (
            <div
              data-testid="communities-card-wrapper"
              className="overflow-y-auto scroll-vertical-small grid grid-cols-1 md:grid-cols-3 gap-3 py-0.5 px-3 max-h-[290px] md:max-h-[314px]"
            >
              {communities.map((community) => (
                <CommunityCheckboxCard
                  community={community}
                  key={`discover-community-community-${community.community.id}`}
                  selectedCommunity={selectedCommunity}
                  setSelectedCommunity={setSelectedCommunity}
                  selectedInterestsCommunity={selectedInterestsCommunity}
                  selectedInterests={selectedInterests}
                />
              ))}
            </div>
          ) : (
            <div
              className="h-[290px] md:h-[314px] flex items-center justify-center"
              data-testid="communities-empty"
            >
              <RevEmptyState title="No Communities" />
            </div>
          )}
        </div>
      </div>
    </DisclosureFilter>
  );
};
